import type RouterService from '@ember/routing/router-service';
import Service, { service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { endOfDay, fromUnixTime, isToday, startOfDay, subMinutes } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import type { TaskForAsyncTaskFunction } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import { getIdFromURN } from 'garaje/models/generic-resource';
import type AjaxService from 'garaje/services/ajax';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import urlBuilder from 'garaje/utils/url-builder';

const POLLING_INTERVAL_MINUTES = 0.5;
// EndTime uses the office end-of-day

export enum ResourceType {
  DESK = 'desk',
  ROOM = 'room',
  POINT_OF_INTEREST = 'poi',
  PARKING = 'parking',
}

export enum CheckInStatusEnum {
  PENDING,
  CHECKED_IN,
}

interface CheckInStatus {
  status: CheckInStatusEnum;
}

export enum AvailabilityStatus {
  AVAILABLE = 'AVAILABLE',
  OCCUPIED = 'OCCUPIED',
  UNAVAILABLE = 'UNAVAILABLE',
}

interface ProfileData {
  avatarUrl: string | null;
  id: string | null; // URN
  name: string | null;
}

interface ResourceStatus {
  reservationId: string | null;
  availabilityStatus: AvailabilityStatus;
  checkInStatus: CheckInStatus | null;
  reservedFor: string | null; // urn:envoy:employee:XXXXX
  reservedForProfileData: ProfileData | null;
}

export interface ResourceAmenity {
  id: number;
  type: string;
  attributes: {
    name: string;
  };
}

export interface ResourceState {
  resourceId: string;
  resourceType: ResourceType;
  assignedTo: string | null; // urn:envoy:employee:XXXXX
  assignedToProfileData: ProfileData | null;
  activeNeighborhoodId: number | null;
  status: ResourceStatus;
  lender: ProfileData | null;
  lendingStatus: string | null;
}

export interface LiveMapResponse {
  data: {
    live: ResourceState[];
    static: unknown; // right now, this is an array of different resources in jsonapi format
  };
}

// time fields are of RFC3339 format which is basically the same as ISO8601
export type LiveMapQueryFilters =
  | {
      locationId?: string;
      areaMapId?: never;
      floorId?: string;
      selectedTime?: number;
      startTime?: string;
      endTime?: string;
    }
  | {
      locationId?: never;
      areaMapId?: string;
      floorId?: string;
      selectedTime?: number;
      startTime?: string;
      endTime?: string;
    };

// This service is responsible for communicating with the live map endpoint to get the current state of resources
// And fetching the static data used in the live map view.
// It fetches and stores live map data in the form of resource states and polls the endpoint every 5 minutes.
// It also fetches and loads the static map data into the ember store
export default class LiveMapService extends Service {
  @service declare ajax: AjaxService;
  @service declare state: StateService;
  @service declare flashMessages: FlashMessagesService;
  @service declare store: Store;
  @service declare router: RouterService;

  @tracked resourceStates: ResourceState[] = [];
  @tracked amenities: ResourceAmenity[] = [];
  @tracked didLiveUpdateFail: boolean = false;
  @tracked currentPollingLocationId: string | null = null;
  @tracked selectedStartTime: string = '';

  // Config for polling
  timeoutId: ReturnType<typeof setTimeout> | null = null; // TS normally expect a Timeout object, but in ember this is a number so this just tells TS to check the environment

  _toOfficeLocationTime(date: Date): Date {
    return this.state.getOfficeLocationTime(date);
  }

  _getStartEndTime(filters?: LiveMapQueryFilters): { startTime: string; endTime: string } {
    // If explicit startTime/endTime are provided, use them
    if (filters?.startTime && filters?.endTime) {
      return {
        startTime: filters.startTime,
        endTime: filters.endTime,
      };
    }

    const selectedTime = Number(filters?.selectedTime || this.router.currentRoute?.queryParams['selectedTime']);
    const selectedTimeDate = fromUnixTime(selectedTime);
    const isTodayByBrowser = isToday(fromUnixTime(selectedTime));

    if (selectedTime && !isTodayByBrowser) {
      const startTime = this._toOfficeLocationTime(
        startOfDay(subMinutes(selectedTimeDate, this.state.minutesBetweenTimezones(selectedTimeDate))),
      ).toISOString();
      const endTime = this._toOfficeLocationTime(
        endOfDay(subMinutes(selectedTimeDate, this.state.minutesBetweenTimezones(selectedTimeDate))),
      ).toISOString();
      return { startTime, endTime };
    }

    // Use office 'now' (wall-clock in office tz) as startTime, and office end-of-day as endTime
    const tz = this.state.currentLocation.timezone;
    const zonedNow = toZonedTime(new Date(), tz);
    const zonedEndOfDay = endOfDay(zonedNow);
    const startUtc = fromZonedTime(zonedNow, tz);
    const endUtc = fromZonedTime(zonedEndOfDay, tz);
    return {
      startTime: startUtc.toISOString(),
      endTime: endUtc.toISOString(),
    };
  }

  fetchLiveMapDataTask = task(async (filters?: LiveMapQueryFilters) => {
    const { startTime, endTime } = this._getStartEndTime(filters);
    const queryFilters = {
      locationId: filters?.locationId || this.state.currentLocation?.id,
      floorId: filters?.floorId,
      startTime: encodeURIComponent(startTime),
      endTime: encodeURIComponent(endTime),
    };

    const liveMapDataEndpoint = urlBuilder.maps.getLiveMapUrl(queryFilters);
    const payload: LiveMapResponse = await this.ajax.request(liveMapDataEndpoint, {
      type: 'GET',
      headers: { Accept: 'application/vnd.api+json' },
    });

    return payload;
  });

  // Get all the resource states for current time
  fetchAndSaveLiveMapDataTask = task({ drop: true }, async (filters?: LiveMapQueryFilters) => {
    this.currentPollingLocationId = filters?.locationId || this.state.currentLocation?.id;

    const payload: LiveMapResponse = await this.fetchLiveMapDataTask.perform(filters);

    // Save live data locally in this service.
    // Push the static data into the ember store.
    this.resourceStates = payload.data.live;
    this.amenities = (payload.data.static as ResourceAmenity[]).filter((s) => s.type === 'amenities');
    this.store.pushPayload({ data: payload.data.static });
  });

  getCurrentResourceStates(): ResourceState[] {
    return this.resourceStates;
  }

  getResourceStatesByType(resourceType: ResourceType): ResourceState[] {
    const resourceStates: ResourceState[] = this.getCurrentResourceStates();
    return resourceStates.filter((resource) => resource.resourceType === resourceType);
  }

  getResourceStateByIdAndType(resourceId: string, resourceType: ResourceType): ResourceState | undefined {
    const resourceStates: ResourceState[] = this.getCurrentResourceStates();
    return resourceStates.find(
      (resource) => resource.resourceId === resourceId && resource.resourceType === resourceType,
    );
  }

  getResourceAvailabilityByIdAndType(resourceId: string, resourceType: ResourceType): AvailabilityStatus | undefined {
    return this.getResourceStateByIdAndType(resourceId, resourceType)?.status?.availabilityStatus;
  }

  getAvailableResourcesByType(resourceType: ResourceType): ResourceState[] {
    const resourceStates: ResourceState[] = this.getCurrentResourceStates();
    return resourceStates.filter(
      (resource) =>
        resource.resourceType === resourceType && resource.status.availabilityStatus === AvailabilityStatus.AVAILABLE,
    );
  }

  getAmenityById(id: number): ResourceAmenity | undefined {
    return this.amenities.find((amenity) => amenity.id === id);
  }

  async getUpdatedLiveMapData(filters?: LiveMapQueryFilters): Promise<void> {
    this.stopPolling();

    // Fetches the latest live map and starts polling management
    await this.startPollingLiveMap(this.fetchAndSaveLiveMapDataTask, filters);
  }

  get currentUserReservations(): ResourceState[] {
    const resourceStates: ResourceState[] = this.getCurrentResourceStates();
    return resourceStates.filter((resource) => {
      return getIdFromURN(resource.status.reservedFor) === this.state.currentAdmin.employee?.id;
    });
  }

  // Gets any user reservation for a given employee
  getUserReservation(employeeId: string): ResourceState | undefined {
    const resourceStates: ResourceState[] = this.getCurrentResourceStates();
    return resourceStates.find((resource) => {
      return getIdFromURN(resource.status.reservedFor) === employeeId;
    });
  }

  stopPolling(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
  // In case a consumer needs to restart the polling cycle
  async restartPollingLiveMap(): Promise<void> {
    this.stopPolling();

    const currentFilters: LiveMapQueryFilters | undefined = this.currentPollingLocationId
      ? this.currentPollingLocationId === this.state.currentLocation?.id
        ? { locationId: this.currentPollingLocationId }
        : { areaMapId: this.currentPollingLocationId }
      : undefined;

    this.didLiveUpdateFail = false;
    this.flashMessages.hideAll();
    await this.startPollingLiveMap(this.fetchAndSaveLiveMapDataTask, currentFilters);
  }

  willDestroy(): void {
    this.stopPolling();
    super.willDestroy();
  }

  private async startPollingLiveMap(
    taskCallback: TaskForAsyncTaskFunction<
      {
        drop: boolean;
      },
      (filters?: LiveMapQueryFilters) => Promise<void>
    >,
    filters?: LiveMapQueryFilters,
  ): Promise<void> {
    try {
      await taskCallback.perform(filters);

      if (this.didLiveUpdateFail) {
        this.didLiveUpdateFail = false;
        this.flashMessages.hideAll();
      }
    } catch (error) {
      if (!this.didLiveUpdateFail) {
        this.didLiveUpdateFail = true;
      }
      this.flashMessages.showFlash('error', 'Live map data could not be fetched');
      // eslint-disable-next-line no-console
      console.error('Failed to fetch resource states', error);
    } finally {
      // Clear existing timeout before setting an ew one
      this.stopPolling();

      this.timeoutId = setTimeout(
        () => void this.startPollingLiveMap(taskCallback, filters),
        POLLING_INTERVAL_MINUTES * 60 * 1000,
      );
    }
  }
}
